/**
 * The interview tracker and the client-safe report.
 *
 * **Order is load-bearing here too.** `interview-client-view` must precede any future
 * `/interviews/$interviewId`, for the same reason the candidate routes are ordered as they are.
 * This is also the only entry carrying a `validateSearch` — declared in the manifest so the
 * router builds it from one place.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const INTERVIEW_ROUTES: readonly RouteManifestEntry[] = [
  // The tracker is gated on `pipeline`, not on `clients`: an interview is a round inside an
  // application, and an agency that has not bought the execution module has no rounds to track.
  {
    id: 'interviews',
    path: '/interviews',
    entitlement: 'pipeline',
    // `interview`, not `application`: §12 names coordinators as users of this screen, and they
    // hold read on `application` alone. The matrix note on the resource has the argument.
    requires: { action: 'read', resource: 'interview' },
    authenticated: true,
    nav: { labelKey: 'interviews.nav', section: 'recruiting', order: 5 },
    /*
     * Change order 2 §11.2: a job's interview section links here with its question already asked.
     *
     * Ids are passed through as strings and validated by the API, not here — a screen that refused
     * a malformed id in the URL bar would 404 where the server would 422, and the server's message
     * is the one that names the field. What this does guarantee is that the two keys survive a
     * reload and a copied link, which is the whole point of them being in the URL.
     */
    /*
     * Every filter, not two of them — roadmap F1, and what makes a saved view possible.
     *
     * Until now `jobId` and `candidateProfileId` lived here and the other five lived in component
     * state, with a comment saying moving them was "the book-of-work debt". That split had two
     * costs and only the first was obvious: a filter set could not be linked or reloaded, and —
     * the real one — **switching between table, agenda and calendar could silently drop one**,
     * because each view read a different subset of the same state. CO4 §1 names that as the defect
     * to test for. One model, one place, and the view itself is in it.
     *
     * A saved view is now exactly a snapshot of this object, which is why `access.saved_view`
     * validates nothing about the params it stores: *this* function is what judges them, on every
     * navigation, and it is the only thing that knows what they mean.
     *
     * Unknown and malformed values are dropped rather than refused. A screen that 404s on a typo
     * in the address bar is worse than one that opens with that filter unset — and a saved view
     * written by an older build must degrade to a usable screen rather than to an error.
     */
    validateSearch: (search) => {
      const text = (value: unknown): string | undefined =>
        typeof value === 'string' && value !== '' ? value : undefined;
      const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
        typeof value === 'string' && (allowed as readonly string[]).includes(value)
          ? (value as T)
          : undefined;

      const jobId = text(search.jobId);
      const candidateProfileId = text(search.candidateProfileId);
      const clientId = text(search.clientId);
      const mode = oneOf(search.mode, ['phone', 'video', 'in_person', 'take_home', 'async']);
      const attention = oneOf(search.attention, [
        'no_outcome',
        'no_feedback',
        'rescheduled_twice',
      ]);
      const view = oneOf(search.view, ['table', 'agenda', 'calendar']);
      const calendarMode = oneOf(search.calendarMode, ['day', 'week']);
      const rowMode = oneOf(search.rowMode, ['round', 'occurrence']);

      /*
       * Free text, capped. Not because a long query is dangerous — it never reaches the server —
       * but because the whole object is what a saved view stores, and `saved_view.params` is bounded
       * at 8KB by a CHECK. A cap here is the difference between a refused save and a truncated one.
       */
      const q = text(search.q)?.slice(0, 200);

      /*
       * A local calendar date. Shape-checked only: whether it is a real date is the screen's
       * problem, and `new Date(2026, 1, 31)` rolling into March is a harmless week to be shown
       * rather than something worth refusing a navigation over.
       */
      const day = /^\d{4}-\d{2}-\d{2}$/.test(String(search.day ?? ''))
        ? String(search.day)
        : undefined;

      /*
       * `columnId:asc`, or several comma-separated — the shape a grouped sort needs.
       *
       * Deliberately not validated against the column list here: the columns are the screen's
       * vocabulary, and this file would be the second copy of it. `parseSort` in the screen drops a
       * column it does not know, so a stale saved view sorts by what it can and ignores the rest.
       * Six keys is the cap, which is more than anybody groups by and short enough that the param
       * cannot become a payload.
       */
      const sort = /^[a-z_]{1,40}:(asc|desc)(,[a-z_]{1,40}:(asc|desc)){0,5}$/.test(
        String(search.sort ?? ''),
      )
        ? String(search.sort)
        : undefined;

      return {
        ...(jobId ? { jobId } : {}),
        ...(candidateProfileId ? { candidateProfileId } : {}),
        ...(clientId ? { clientId } : {}),
        ...(mode ? { mode } : {}),
        ...(attention ? { attention } : {}),
        ...(view ? { view } : {}),
        ...(calendarMode ? { calendarMode } : {}),
        ...(rowMode ? { rowMode } : {}),
        ...(q ? { q } : {}),
        ...(sort ? { sort } : {}),
        ...(day ? { day } : {}),
        /*
         * The one filter whose default is *on*, so absence cannot mean "unset".
         *
         * `openOnly` opens true — §12's in-flight view — which means the URL has to be able to say
         * false explicitly. A key that only ever appeared when true would make "show me everything"
         * unlinkable and unsaveable, which is the state somebody most often wants to share.
         */
        openOnly: search.openOnly === false || search.openOnly === 'false' ? false : true,
      };
    },
    load: () => import('../screens/interviews.js'),
  },
  // Before `/interviews/$interviewId` below, for the reason the candidate routes record: a router
  // is free to read a literal segment as a parameter, and the screen then asks the API for an
  // interview whose id is the word "client-view". The ordering was set before the detail route
  // existed precisely so that adding one could not break it — and the first draft of that addition
  // broke it anyway, caught by the assertion in `interviews.test.ts` rather than by a reader.
  {
    id: 'interview-client-view',
    path: '/interviews/client-view',
    entitlement: 'pipeline',
    requires: { action: 'read', resource: 'interview' },
    authenticated: true,
    load: () => import('../screens/client-view.js'),
  },
  /*
   * One round, addressable — CO4 §5, and `pipeline.interview.read` is what made it possible.
   *
   * Last, and it must stay last: every literal path under `/interviews/` has to be matched before
   * the one that accepts anything as an id.
   */
  {
    id: 'interview-detail',
    path: '/interviews/$interviewId',
    entitlement: 'pipeline',
    requires: { action: 'read', resource: 'interview' },
    authenticated: true,
    load: () => import('../screens/interview-detail.js'),
  },
];
